const warnedProps = new Set<string>();

function warnAbsorbedAccess(prop: string | symbol) {
  if (process.env.NODE_ENV !== 'development') return;
  const key = String(prop);
  if (warnedProps.has(key)) return;
  warnedProps.add(key);
  // eslint-disable-next-line no-console -- dev-only visibility into silently absorbed access
  console.warn(
    `[no-code] read-only expression tried to use "${key}" — the access was ignored. Bindings are formulas; use an action (evalJS/executeScript) for side effects.`,
  );
}

// Coercion must yield a primitive or the engine throws a TypeError mid-expression,
// which would turn "silently absorb" into a new error path.
function coercionStub(prop: string | symbol): (() => string) | (() => undefined) | null {
  if (prop === Symbol.toPrimitive || prop === 'toString') return () => '';
  if (prop === 'valueOf') return () => undefined;
  if (prop === 'toJSON') return () => undefined;
  return null;
}

const absorberTarget = function absorbed() {
  /* placeholder target; every behavior lives in the proxy traps */
};

// One shared sink for everything a read-only expression may not touch. Reads return
// the absorber again (chains die quietly at any depth), writes/deletes are swallowed,
// calls and constructions return the absorber. It is function-typed on purpose:
// sanitizeEvalResult already drops function values from binding results, so a leaked
// absorber renders as undefined instead of something weird.
export const absorber: unknown = new Proxy(absorberTarget, {
  get(_target, prop) {
    const stub = coercionStub(prop);
    if (stub) return stub;
    if (prop === Symbol.toStringTag) return undefined;
    warnAbsorbedAccess(prop);
    return absorber;
  },
  set(_target, prop) {
    warnAbsorbedAccess(prop);
    return true;
  },
  defineProperty() {
    return true;
  },
  deleteProperty() {
    return true;
  },
  apply() {
    return absorber;
  },
  construct() {
    return absorber as object;
  },
});

export { coercionStub };
